import { useState } from "react";
import { Lock, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/leuteliaApi";

export const MODULE_LABEL = { sito: "Analisi del sito", target: "Target", branding: "Branding", seo: "SEO", marketing: "Marketing", fotografia: "Fotografia", google: "Google", ota: "OTA", social: "Social", recensioni: "Recensioni", panoramica: "Panoramica", priorita: "Priorità", reparti: "Direzione & Reparti" };

export function moduleAllowed(settings, id) {
  const mods = settings?.pacchetto?.modules;
  if (!mods) return true;
  const base = String(id).split(":")[0];
  if (base === "reparti" || base === "reparto" || base === "risultati") return mods.includes("reparti");
  if (base === "analisi") return mods.includes("panoramica");
  return mods.includes(base) || ["report", "pacchetti", "impostazioni", "aiuto", "amministrazione", "gruppo", "soluzioni"].includes(base);
}

const euro = (n) => (n == null ? "su progetto" : `${n.toLocaleString("it-IT")} € / mese`);

export default function PacchettoBloccato({ module, settings, catalog }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = settings?.pacchetto;
  const target = (catalog || []).find((p) => p.modules.includes(module)) || (catalog || [])[0];
  const label = MODULE_LABEL[module] || module;
  const ask = async () => {
    setBusy(true);
    try { await api.pacchettoUpgrade({ module }); setSent(true); toast.success("Richiesta inviata: vi ricontattiamo entro un giorno lavorativo."); }
    catch (e) { toast.error(e.message || "Invio non riuscito"); } finally { setBusy(false); }
  };
  if (!target) return null;
  return (
    <section className="max-w-5xl" data-testid={`pacchetto-bloccato-${module}`}>
      <div className="rounded-2xl bg-brand-green text-brand-cream p-7 md:p-10 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8 items-center">
        <img src={target.image} alt={`Pacchetto ${target.name}`} className="w-full max-w-[260px] mx-auto drop-shadow-2xl" data-testid="pacchetto-immagine" />
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-gold font-bold"><Lock size={13} /> {label} non è incluso nel vostro pacchetto {current?.name}</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-3">Il modulo {label} è compreso nel pacchetto {target.name}</h1>
          <p className="text-[15px] text-brand-cream/85 mt-3 leading-relaxed">{target.pitch}</p>
          <div className="mt-5 flex flex-wrap items-end gap-4">
            <div><div className="text-3xl font-serif">{euro(target.price)}</div>
              {target.regime_price != null && <div className="text-[12px] text-brand-cream/70">Tariffa Beta · prezzo previsto a regime {target.regime_price.toLocaleString("it-IT")} € / mese</div>}</div>
            {target.hotels > 1 && <div className="text-[12.5px] text-brand-cream/80">fino a {target.hotels} hotel</div>}
          </div>
        </div>
      </div>
      <ServiziPacchetto target={target} module={module} />
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={ask} disabled={busy || sent} className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-green px-5 py-2.5 text-[13.5px] font-semibold hover:brightness-110 disabled:opacity-60" data-testid="richiedi-upgrade-btn"><Mail size={15} /> {sent ? "Richiesta inviata" : `Richiedi il pacchetto ${target.name}`}</button>
        <span className="text-[12px] text-brand-green/70 self-center">Nessun vincolo: vi ricontatta l'amministrazione LEUTELIA entro un giorno lavorativo.</span>
      </div>
    </section>
  );
}

function ServiziPacchetto({ target, module }) {
  const servizi = target.servizi || [];
  const inclusi = servizi.filter((s) => s.incluso && !s.sempre);
  const sempre = servizi.filter((s) => s.sempre);
  const esclusi = servizi.filter((s) => !s.incluso);
  const Riga = ({ s, muted }) => (
    <li className={`flex gap-3 rounded-xl p-3 ${s.id === module ? "bg-brand-gold/15 ring-1 ring-brand-gold/50" : "bg-white/60"} ${muted ? "opacity-75" : ""}`} data-testid={`servizio-${s.id}`}>
      {muted ? <Lock size={15} className="mt-0.5 shrink-0 text-brand-green/50" /> : <Check size={15} className="mt-0.5 shrink-0 text-brand-gold" />}
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-brand-green">{s.label}{muted && s.da && <span className="ml-2 text-[11px] font-medium uppercase tracking-wider text-brand-green/60">incluso dal pacchetto {s.da}</span>}</div>
        <div className="text-[12.5px] leading-relaxed text-brand-green/75">{s.desc}</div>
      </div>
    </li>
  );
  return (
    <div className="mt-6 grid gap-6 md:grid-cols-[1fr_1fr]" data-testid="servizi-pacchetto">
      <div>
        <h2 className="text-[11px] uppercase tracking-[0.22em] text-brand-green/60 font-bold">Cosa comprende il pacchetto {target.name}</h2>
        <ul className="mt-3 space-y-2">{inclusi.map((s) => <Riga key={s.id} s={s} />)}</ul>
        <h2 className="mt-5 text-[11px] uppercase tracking-[0.22em] text-brand-green/60 font-bold">Sempre compresi, in ogni pacchetto</h2>
        <ul className="mt-3 space-y-2">{sempre.map((s) => <Riga key={s.id} s={s} />)}</ul>
      </div>
      {esclusi.length > 0 && (
        <div>
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-brand-green/60 font-bold">Non compresi in {target.name}</h2>
          <ul className="mt-3 space-y-2">{esclusi.map((s) => <Riga key={s.id} s={s} muted />)}</ul>
        </div>
      )}
    </div>
  );
}
